import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ZERO,
  fractionClamp,
  fractionLte,
  fractionMax,
  fractionMin,
} from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";

/**
 * A funding source where the available balance is know upfront. This is the
 * case for lenders that don't us e retained interest or if the interest
 * allowance is know upfront.
 */
export interface CashflowFundSource<Key> {
  key: Key;
  /**
   * The available amount (positive) offered by the lender.
   */
  availableBalance: BigFraction;
}

export const cashflowsToBalances = (cashflows: ReadonlyArray<BigFraction>) =>
  cashflows.reduce((balances, cashflow, periodIndex) => {
    balances[periodIndex] = cashflow.add(
      balances[periodIndex - 1] ?? FRACTION_ZERO,
    );
    return balances;
  }, [] as Array<BigFraction>);

export const balancesToCashflows = (balances: ReadonlyArray<BigFraction>) =>
  balances.reduce((cashflows, balance, periodIndex) => {
    cashflows[periodIndex] = balance.sub(
      balances[periodIndex - 1] ?? FRACTION_ZERO,
    );
    return cashflows;
  }, [] as Array<BigFraction>);

const computeLenderBalances = (params: {
  balances: ReadonlyArray<BigFraction>;
  /**
   * The minimum (negative) balance the lender can reach.
   */
  lenderMinimumBalance: BigFraction;
}) => {
  if (!fractionLte(params.lenderMinimumBalance, FRACTION_ZERO)) {
    throw new Error(
      `lenderMinimumBalance should be negative ${params.lenderMinimumBalance.valueOf()}`,
    );
  }

  // We are trying to find The minimum equity with full lender's facility mobilised.
  // To do this we find the lowest balance for equity and calculate the new
  // minimum once the lender's facility is fully used.
  const newMinEquity = fractionMin(
    params.balances[0] ?? FRACTION_ZERO,
    ...params.balances,
  ).sub(params.lenderMinimumBalance);

  // Then we calculate the new equity balances capping (at the bottom) it with
  // the newly found minimum.
  const leveragedBalances = params.balances.map((balance) =>
    fractionMax(newMinEquity, balance),
  );

  // Then we iterate throughout the balance, subtracting the newly calculated
  // leveraged balance for each period which will give use the balances for the
  // current lender.
  const lenderBalances = params.balances.map((balance, periodIndex) =>
    fractionMin(balance, FRACTION_ZERO).sub(
      fractionMin(expectAtIndex(leveragedBalances, periodIndex), FRACTION_ZERO),
    ),
  );

  return { lenderBalances, leveragedBalances };
};

/**
 * @param projectBalances
 * @param lenders
 * @param equityBalanceTarget the ideal equity that should be used (given as
 * positive number). This is only a hint. If the cashflow needs more equity it
 * will be reflected in the returned `equityBalances`. On the other hand if it
 * had required less equity it still makes sure to use up to the given target.
 */
export const computeCashflow = <Key>(
  projectBalances: ReadonlyArray<BigFraction>,
  lenders: ReadonlyArray<CashflowFundSource<Key>>,
  equityBalanceTarget = FRACTION_ZERO,
) => {
  // The tuples of lenders + balances from the highest priority to the lowest
  const lendersBalances: Array<
    [key: Key, balances: ReadonlyArray<BigFraction>]
  > = [];

  let carryOverBalances = projectBalances;

  for (let idxLender = 0; idxLender < lenders.length; idxLender++) {
    const currentLender = expectAtIndex(lenders, idxLender);
    const restLenders = lenders.slice(idxLender + 1);

    const minBalance = fractionMin(
      carryOverBalances[0] ?? FRACTION_ZERO,
      ...carryOverBalances,
    );

    // the sum of the minimum balances offered by the lower priority sources
    const offset = sum([
      ...restLenders.map(({ availableBalance }) => availableBalance.neg()),
      equityBalanceTarget.neg(),
    ]);

    const lenderMinimumBalance = fractionClamp(
      currentLender.availableBalance.neg(),
      minBalance.sub(offset ?? FRACTION_ZERO),
      FRACTION_ZERO,
    );

    const { lenderBalances, leveragedBalances } = computeLenderBalances({
      balances: carryOverBalances,
      lenderMinimumBalance,
    });

    lendersBalances.push([currentLender.key, lenderBalances]);

    carryOverBalances = leveragedBalances;
  }

  // the leftover balance is financed by the developer
  return { equityBalances: carryOverBalances, lendersBalances };
};
